const PlugAriaUtils = () => {
    let ignoreUtilFocusChanges = false;

    /**
     * @param {boolean} value
     */
    const setIgnoreUtilFocusChanges = (value) => {
        ignoreUtilFocusChanges = value;
    };

    const getIgnoreUtilFocusChanges = () => {
        return ignoreUtilFocusChanges;
    };

    const isFocusable = (element) => {
        if (element.tabIndex < 0) {
            return false;
        }
        if ("disabled" in element && element.disabled) {
            return false;
        }
        switch (element.nodeName) {
            case "A":
                return (
                    !!element.href
                    && element.rel !== "ignore"
                );
            case "INPUT":
                return element.type !== "hidden";
            case "BUTTON":
            case "SELECT":
            case "TEXTAREA":
                return true;
            default:
                return false;
        }
    };

    const attemptFocus = (element) => {
        if (!isFocusable(element)) {
            return false;
        }
        ignoreUtilFocusChanges = true;
        try {
            element.focus();
        } catch (e) {
            // continue regardless of error
        }
        ignoreUtilFocusChanges = false;
        return document.activeElement === element;
    };

    const focusFirstDescendant = (element) => {
        for (let i = 0; i < element.childNodes.length; i++) {
            let child = element.childNodes[i];
            if (
                attemptFocus(child) ||
                focusFirstDescendant(child)
            ) {
                return true;
            }
        }
        return false;
    };

    const focusLastDescendant = (element) => {
        for (let i = element.childNodes.length - 1; i >= 0; i--) {
            let child = element.childNodes[i];
            if (
                attemptFocus(child) ||
                focusLastDescendant(child)
            ) {
                return true;
            }
        }
        return false;
    };

    return {
        setIgnoreUtilFocusChanges,
        getIgnoreUtilFocusChanges,
        isFocusable,
        attemptFocus,
        focusFirstDescendant,
        focusLastDescendant,
    }
};

class PlugModal extends HTMLElement {
    // Important things to account for:
    //  - Properly using aria attributes.
    //  - Properly using focus.
    //  - Properly using keyboard navigation.

    constructor() {
        super();
        this.onCloseHandlers = [];
        this.ariaUtils = PlugAriaUtils();
    }

    connectedCallback() {
        this.elements = {
            overlay: this.getElementsByClassName("plug-modal__overlay")[0],
            closeButton: this.getElementsByClassName("plug-modal__close")[0],
            firstFocus: this.querySelector(`[data-focus-first]`),
        };

        console.log({firstFocus: this.elements.firstFocus});

        if (this.dataset.focusAfterClose) {
            this.elementToFocusAfterClose = document.querySelector(this.dataset.focusAfterClose);
        }

        this.removeListeners = this.initListeners();
    }

    disconnectedCallback() {
        if (typeof this.removeListeners === "function") {
            this.removeListeners();
        }
    }

    // Called when a focus event is triggered while the modal is open.
    trapFocus(e) {
        // If we are ignoring focus changes, ignore this event.
        if (this.ariaUtils.getIgnoreUtilFocusChanges()) {
            return;
        }

        // If the focused element is inside this modal.
        if (this.contains(e.target)) {
            // Set lastFocus equal to the newly focused element.
            this.lastFocus = e.target;
            // Otherwise, the focused element is outside this modal.
        } else {
            // Focus the first focusable element in this modal.
            this.ariaUtils.focusFirstDescendant(this);
            // If the first focusable element was the last focused element.
            if (this.lastFocus === document.activeElement) {
                // Focus the last focusable element in this modal.
                this.ariaUtils.focusLastDescendant(this);
            }
            // Update lastFocus.
            this.lastFocus = document.activeElement;
        }
    }

    initTemporaryListeners() {
        const handleFocus = (e) => {
            this.trapFocus(e);
        };
        document.addEventListener("focus", handleFocus, false);

        const handleEscape = (e) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                this.close();
            }
        };
        document.addEventListener("keyup", handleEscape, false);

        return () => {
            document.removeEventListener("focus", handleFocus, false);
            document.removeEventListener("keyup", handleEscape, false);
        };
    }

    initListeners() {
        const onClose = () => {
            this.close();
        };

        if (this.elements.closeButton) {
            this.elements.closeButton.addEventListener("click", onClose, false);
        } else {
            console.warn("Cannot find closeButton for PlugModal.");
        }

        if (this.elements.overlay) {
            this.elements.overlay.addEventListener("click", onClose, false);
        } else {
            console.warn("Cannot find overlay for PlugModal.");
        }

        return () => {
            if (this.elements.closeButton) {
                this.elements.closeButton.removeEventListener("click", onClose, false);
            }
            if (this.elements.overlay) {
                this.elements.overlay.removeEventListener("click", onClose, false);
            }
        };
    }

    onClose(handler) {
        this.onCloseHandlers.push(handler);
    }

    bracketModal() {
        // Bracket the dialog node with two invisible, focusable nodes.
        // While this dialog is open, we use these to make sure that focus never
        // leaves the document even if dialogNode is the first or last node.
        const preDiv = document.createElement("div");
        this.preNode = this.parentNode.insertBefore(preDiv, this);
        this.preNode.tabIndex = 0;
        const postDiv = document.createElement("div");
        this.postNode = (
            this.nextSibling
                ? this.parentNode.insertBefore(postDiv, this.nextSibling)
                : this.parentNode.appendChild(postDiv)
        );
        this.postNode.tabIndex = 0;
    }

    open() {
        this.dataset.isOpen = "true";
        if (this.dataset.focusAfterClose) {
            this.elementToFocusAfterClose = document.querySelector(this.dataset.focusAfterClose);
        }
        document.body.classList.add("plug-no-scroll");

        window.requestAnimationFrame(() => {
            setTimeout(() => {
                this.dataset.isViewable = "true";
                if (this.elements.firstFocus) {
                    this.bracketModal();
                    this.elements.firstFocus.focus();
                    this.removeTemporaryListeners = this.initTemporaryListeners();
                }
            }, 0);
        });
    }

    close() {
        this.dataset.isOpen = "false";
        this.dataset.isViewable = "false";
        document.body.classList.remove("plug-no-scroll");
        if (typeof this.removeTemporaryListeners === "function") {
            this.removeTemporaryListeners();
        }
        if (this.preNode) {
            this.preNode.parentElement.removeChild(this.preNode);
        }
        if (this.postNode) {
            this.postNode.parentElement.removeChild(this.postNode);
        }
        if (this.onCloseHandlers.length) {
            this.onCloseHandlers.forEach(handler => {
                // The context in which the handler was created may set it equal to undefined at some point,
                // so we check to make sure that hasn't happened before calling it.
                if (typeof handler === "function") {
                    handler();
                }
            });
        }

        if (this.elementToFocusAfterClose) {
            this.elementToFocusAfterClose.focus();
        }
    }
}

if (!customElements.get("plug-modal")) {
    customElements.define("plug-modal", PlugModal);
}

class PlugBackInStockModal extends PlugModal {
    constructor() {
        super();
    }

    connectedCallback() {
        super.connectedCallback()
        this.elements.errorMessage = this.querySelector(".plug-back-in-stock-error");
        this.elements.successMessage = this.querySelector(".plug-back-in-stock-success");
        this.elements.submitButton = this.querySelector("button[type='submit']");
        this.elements.form = this.querySelector("form");
        this.elements.successClose = this.querySelector(".plug-back-in-stock-success__close");
        if (this.elements.form) {
            this.elements.form.addEventListener("submit", (e) => {
                e.preventDefault();
                const email = this.elements.form.querySelector("#plug-back-in-stock-email").value;
                const variantId = this.dataset.variantId;
                this.sendRequestToKlaviyo(email, variantId);
            });
        }
        if (this.elements.successClose) {
            this.elements.successClose.addEventListener("click", (e) => {
                e.preventDefault();
                this.close();
            });
        }
    }

    open() {
        const title = this.querySelector(".plug-modal__title");
        title.textContent = this.dataset.productTitle || "Notify me when back in stock";
        this.elements.errorMessage.classList.add("is-hidden");
        this.elements.successMessage.classList.add("is-hidden");
        this.elements.submitButton.classList.remove("is-hidden");
        super.open();
    }

    sendRequestToKlaviyo(email, variantId) {
        const body = {
            "data": {
                "type": "back-in-stock-subscription",
                "attributes": {
                    "profile": {
                        "data": {
                            "type": "profile",
                            "attributes": {
                                "email": email
                            }
                        }
                    },
                    "channels": ["EMAIL"],
                },
                "relationships": {
                    "variant": {
                        "data": {
                            "type": "catalog-variant",
                            "id": `$shopify:::$default:::${variantId}`
                        }
                    }
                }
            }
        }

        const requestOptions = {
            method: 'POST',
            headers: {
                "Content-Type": "application/json",
                "revision":"2024-06-15"
            },
            body: JSON.stringify(body),
        };

        fetch("https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=Wi2C2r", requestOptions)
            .then(result => {
                if (result.ok) {
                    console.log("PLUG: Successfully sent back-in-stock request to Klaviyo.");
                    this.elements.submitButton.classList.add("is-hidden");
                    this.elements.successMessage.classList.remove("is-hidden");
                    this.elements.errorMessage.classList.add("is-hidden");
                } else {
                    console.error("PLUG: Failed to send back-in-stock request to Klaviyo.", result);
                    this.elements.successMessage.classList.add("is-hidden");
                    this.elements.errorMessage.classList.remove("is-hidden");
                }
            })
            .catch(error => console.log('error', error));
    }

    disconnectedCallback() {
        super.disconnectedCallback();
    }
}

if (!customElements.get("plug-back-in-stock-modal")) {
    customElements.define("plug-back-in-stock-modal", PlugBackInStockModal);
}

class PlugBackInStockButton extends HTMLElement {
    constructor() {
        super();
        this.modal = null;
    }

    connectedCallback() {
        this.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopImmediatePropagation();

            const modal = document.querySelector(".plug-back-in-stock-modal");
            if (modal) {
                modal.dataset.focusAfterClose = `[data-id="${this.dataset.id}"]`;
                modal.dataset.productTitle = this.dataset.productTitle;
                modal.dataset.variantId = this.dataset.variantId;
                modal.open();
            } else {
                console.error("Modal element not found.");
            }
        });
    }
}

if (!customElements.get("plug-back-in-stock-button")) {
    customElements.define("plug-back-in-stock-button", PlugBackInStockButton);
}
